// Prebuilt formats in «Crear creativo» (report 109): the gallery by category and the simple configuration of the
// chosen format. A card and its «Ver demostración» show the format's COMMERCIAL demo (a finished ad of a fictional
// brand, run by the served Creative Runtime: demos/); the configuration previews the editable template with the
// Studio's own player (`InteractivePreview`) on the document the format really builds, which is what the person
// gets. The demo's brand is never copied. Nothing here calls a model; uploads go to the brand library.
import { useQueryClient } from "@tanstack/react-query";
import { Eye, Film, ImageIcon, Loader2, RotateCcw, Upload } from "lucide-react";
import { ASSET_UPLOAD_LIMITS } from "@interactads/domain";
import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { ApiProblem } from "@/api/errors";
import { COMMON_FORMATS, PRODUCTS } from "@/lib/labels";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { Dialog, DialogContent } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs";
import { assetUrlFor, pinnedRef, studioKeys, uploadAsset, useBrandAssets } from "../studio/api";
import { StudioContext, type StudioContextValue } from "../studio/ui/context";
import { InteractivePreview } from "../studio/ui/preview-player";
import { FORMAT_DEMOS, layoutLabel, loadDemo, variantOf, type BuiltDemo } from "./demos/catalog";

// The demo player (and the served runtime's DOM renderer) is loaded only when a demo is shown.
const DemoPlayer = lazy(() => import("./demos/demo-player"));
import {
  CATEGORY_ORDER,
  PREBUILT,
  baseDocument,
  buildPrebuilt,
  worksIn,
  type MediaSlot,
  type PickedAsset,
  type Prebuilt,
  type PrebuiltMedia,
  type PrebuiltValues,
} from "./prebuilt";

/**
 * Files chosen on this computer while the brand does not exist yet (a brand-new workspace): keyed by the
 * placeholder revision id of their reference. They are previewed from the browser and uploaded to the brand
 * library when the creative is created; a placeholder id never reaches the server.
 */
export type LocalFiles = ReadonlyMap<string, File>;

/** The two things the Studio's player reads from its context, for a player outside the Studio. */
function PreviewContext({ orgId, local, children }: { orgId: string; local?: LocalFiles; children: ReactNode }) {
  const urls = useRef(new Map<File, string>());
  useEffect(() => {
    const created = urls.current;
    return () => {
      for (const url of created.values()) URL.revokeObjectURL(url);
      created.clear();
    };
  }, []);
  const value = useMemo(() => {
    const remote = assetUrlFor(orgId);
    const assetUrl: StudioContextValue["assetUrl"] = (ref) => {
      const file = local?.get(ref.revisionId);
      if (!file) return remote(ref);
      let url = urls.current.get(file);
      if (!url) {
        url = URL.createObjectURL(file);
        urls.current.set(file, url);
      }
      return url;
    };
    return { orgId, assetUrl, loadedFonts: new Set<string>() } as unknown as StudioContextValue;
  }, [orgId, local]);
  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}

const ACCEPT = { video: ["video/mp4", "video/webm"], image: ["image/png", "image/jpeg", "image/webp"] } as const;

/** Reads what a reference needs from a file on this computer (the server checks it again on upload). */
async function describeLocal(file: File, kind: "image" | "video"): Promise<PickedAsset> {
  if (!(ACCEPT[kind] as readonly string[]).includes(file.type))
    throw new Error(kind === "video" ? "Usa un video MP4 o WebM." : "Usa una imagen PNG, JPG o WebP.");
  if (file.size > ASSET_UPLOAD_LIMITS[kind])
    throw new Error(`Pesa más de ${String(ASSET_UPLOAD_LIMITS[kind] / 1024 / 1024)} MB.`);
  const sha256 = [...new Uint8Array(await crypto.subtle.digest("SHA-256", await file.arrayBuffer()))]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const url = URL.createObjectURL(file);
  try {
    const size = await new Promise<{ width: number; height: number; durationMs?: number }>((resolve, reject) => {
      if (kind === "image") {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
          reject(new Error("No se pudo leer la imagen."));
        };
        img.src = url;
      } else {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          const seconds = Number.isFinite(video.duration) ? video.duration : 0;
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            durationMs: Math.max(1, Math.round(seconds * 1000)),
          });
        };
        video.onerror = () => {
          reject(new Error("Este navegador no puede reproducir el video."));
        };
        video.src = url;
      }
    });
    return {
      assetId: crypto.randomUUID(),
      revisionId: crypto.randomUUID(),
      sha256,
      kind,
      mime: file.type,
      bytes: file.size,
      ...size,
      role: "content",
      name: file.name,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The sizes a format is offered in: only those where it builds and runs. */
export const sizesOf = (p: Prebuilt) => COMMON_FORMATS.filter((f) => worksIn(p, f));

function Player({ doc, box, bare = false }: { doc: ReturnType<typeof buildPrebuilt>; box: number; bare?: boolean }) {
  const layout = doc.layouts[0];
  if (!layout || layout.sizing.mode !== "fixed") return null;
  const { width, height } = layout.sizing;
  const scale = Math.min(1, box / Math.max(width, height));
  return (
    <div className="grid place-items-center overflow-hidden" style={{ minHeight: Math.round(height * scale) + 8 }}>
      <InteractivePreview doc={doc} layoutId={layout.id} scale={scale} compact bare={bare} />
    </div>
  );
}

/** The format as it really is, with its defaults, in its first size (a stand-in video only for the video banner). */
function sampleDoc(p: Prebuilt) {
  const format = sizesOf(p).find((f) => f === "300x250") ?? sizesOf(p)[0];
  const base = format ? baseDocument(p.category, format, p.experience.name) : null;
  if (!base) return null;
  try {
    return buildPrebuilt(p, base, {}, p.requires === "video" ? { video: SAMPLE_VIDEO } : {});
  } catch {
    return null;
  }
}
// A gallery card never plays a customer's file: the video banner shows its layout with an empty video frame.
const SAMPLE_VIDEO: PickedAsset = {
  assetId: "01900000-0000-7000-8000-00000000000a",
  revisionId: "01900000-0000-7000-8000-00000000000b",
  sha256: "0".repeat(64),
  kind: "video",
  mime: "video/mp4",
  bytes: 1,
  width: 1280,
  height: 720,
  durationMs: 10_000,
  role: "content",
  name: "Tu video",
};

export function PrebuiltGallery({ orgId, onUse }: { orgId: string; onUse: (p: Prebuilt) => void }) {
  const [demo, setDemo] = useState<Prebuilt | null>(null);
  return (
    <PreviewContext orgId={orgId}>
      <Tabs defaultValue={CATEGORY_ORDER[0] ?? "RICH_MEDIA_STANDARD"} className="grid gap-4">
        <TabsList aria-label="Categorías de formatos">
          {CATEGORY_ORDER.map((c) => (
            <TabsTrigger key={c} value={c}>
              {PRODUCTS[c].label}
            </TabsTrigger>
          ))}
        </TabsList>
        {CATEGORY_ORDER.map((c) => (
          <TabsContent
            key={c}
            value={c}
            className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
            data-testid={`prebuilt-${c}`}
          >
            {PREBUILT.filter((p) => p.category === c).map((p) => (
              <PrebuiltCard key={p.id} p={p} onUse={onUse} onDemo={setDemo} />
            ))}
          </TabsContent>
        ))}
      </Tabs>
      <Dialog
        open={demo !== null}
        onOpenChange={(open) => {
          if (!open) setDemo(null);
        }}
      >
        {demo ? (
          <DialogContent
            title={`Demostración: ${demo.experience.name}`}
            description="Un anuncio terminado con este formato. Pruébalo: no se guarda ni se mide nada."
            className="top-[3vh] max-h-[94vh] max-w-4xl overflow-y-auto"
          >
            <DemoBody p={demo} />
            <Button
              variant="primary"
              className="justify-self-end"
              onClick={() => {
                setDemo(null);
                onUse(demo);
              }}
            >
              Usar este formato
            </Button>
          </DialogContent>
        ) : null}
      </Dialog>
    </PreviewContext>
  );
}

/** What the person changes in this format without AI: its fields and its files, in their words. */
export function customizableOf(p: Prebuilt): string[] {
  return [...p.fields.map((f) => f.label), ...p.media.map((m) => MEDIA_LABELS[m])];
}

function useDemo(p: Prebuilt) {
  const entry = FORMAT_DEMOS[p.id];
  const [loaded, setLoaded] = useState<{ id: string; demo: BuiltDemo | null } | null>(null);
  useEffect(() => {
    if (!entry) return;
    let live = true;
    void loadDemo(entry.demo).then((demo) => {
      if (live) setLoaded({ id: entry.demo, demo });
    });
    return () => {
      live = false;
    };
  }, [entry]);
  // undefined = still loading; null = this format has no demo (or it failed to load).
  const demo = !entry ? null : loaded?.id === entry.demo ? loaded.demo : undefined;
  return { entry, demo };
}

/** The commercial demo of the format: interactive, restartable, in each of its sizes. Nothing is saved or sent. */
function DemoBody({ p }: { p: Prebuilt }) {
  const { entry, demo } = useDemo(p);
  const [layoutId, setLayoutId] = useState(entry?.cardLayout ?? "");
  const [run, setRun] = useState(0);
  const [link, setLink] = useState<string | null>(null);
  const [box, setBox] = useState(() => ({ w: Math.min(560, window.innerWidth - 64), h: window.innerHeight * 0.62 }));
  useEffect(() => {
    const onResize = () => {
      setBox({ w: Math.min(560, window.innerWidth - 64), h: window.innerHeight * 0.62 });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);
  if (demo === undefined) return <p className="text-sm text-muted">Cargando la demostración…</p>;
  if (!demo || !entry) return <p className="text-sm text-muted">Demostración no disponible.</p>;
  const variant = variantOf(demo, layoutId);
  if (!variant) return <p className="text-sm text-muted">Demostración no disponible.</p>;
  const scale = Math.min(1, box.w / variant.width, box.h / variant.height);
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_16rem]" data-testid="prebuilt-demo">
      <div className="grid content-start justify-items-center gap-3">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <div role="radiogroup" aria-label="Tamaño de la demostración" className="flex flex-wrap gap-1">
            {demo.variants.map((v) => (
              <button
                key={v.layoutId}
                type="button"
                role="radio"
                aria-checked={v.layoutId === variant.layoutId}
                className={cn(
                  "rounded-md border px-2 py-1 text-xs",
                  v.layoutId === variant.layoutId
                    ? "border-brand bg-brand/10 font-medium text-fg"
                    : "border-line text-muted hover:border-line-strong"
                )}
                onClick={() => {
                  setLayoutId(v.layoutId);
                  setLink(null);
                }}
              >
                {layoutLabel(v)}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setRun((n) => n + 1);
              setLink(null);
            }}
          >
            <RotateCcw aria-hidden="true" /> Reiniciar
          </Button>
        </div>
        <div className="grid place-items-center rounded-md bg-sunken p-3">
          <Suspense
            fallback={
              <div
                className="animate-pulse rounded-sm bg-line/40"
                style={{ width: variant.width * scale, height: variant.height * scale }}
              />
            }
          >
            <DemoPlayer
              demo={demo}
              variant={variant}
              scale={scale}
              run={run}
              onLink={(url) => {
                setLink(url ?? "");
              }}
            />
          </Suspense>
        </div>
        <p className="min-h-5 text-center text-xs text-muted" aria-live="polite">
          {link === null
            ? scale < 1
              ? `Tamaño real ${layoutLabel(variant)}, reducido para caber en tu pantalla.`
              : `Tamaño real ${layoutLabel(variant)}.`
            : `En un anuncio publicado, este botón abriría ${link || "la página del anunciante"}. Aquí no se abre nada.`}
        </p>
      </div>
      <div className="grid content-start gap-3 text-sm">
        <p>{entry.pitch}</p>
        <div className="grid gap-1">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">
            Al usar este formato personalizas
          </h3>
          <ul className="grid list-disc gap-0.5 pl-5 text-muted">
            {customizableOf(p).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
        <p className="rounded-md border border-line bg-sunken p-2 text-xs text-muted">
          {entry.brand} es una marca ficticia creada para esta demostración. Tu anuncio se crea con tus textos, colores,
          imágenes y enlace: nada de esta marca se copia.
        </p>
      </div>
    </div>
  );
}

/** The card's thumbnail: the commercial demo playing (no input); the template's own look if it has no demo. */
function CardThumb({ p, doc }: { p: Prebuilt; doc: ReturnType<typeof buildPrebuilt> | null }) {
  const { entry, demo } = useDemo(p);
  const variant = demo && entry ? variantOf(demo, entry.cardLayout) : undefined;
  if (demo && variant) {
    const scale = Math.min(1, 170 / Math.max(variant.width, variant.height), 260 / variant.width);
    return (
      <div className="grid place-items-center" style={{ minHeight: Math.round(variant.height * scale) + 8 }}>
        <Suspense fallback={<div className="h-[142px] w-[170px] animate-pulse rounded-sm bg-line/40" />}>
          <DemoPlayer demo={demo} variant={variant} scale={scale} interactive={false} />
        </Suspense>
      </div>
    );
  }
  if (demo === undefined && entry) return <div className="h-[178px] animate-pulse rounded-sm bg-line/40" />;
  return doc ? <Player doc={doc} box={170} bare /> : null;
}

function PrebuiltCard({
  p,
  onUse,
  onDemo,
}: {
  p: Prebuilt;
  onUse: (p: Prebuilt) => void;
  onDemo: (p: Prebuilt) => void;
}) {
  const doc = useMemo(() => sampleDoc(p), [p]);
  const sizes = useMemo(() => sizesOf(p), [p]);
  const brand = FORMAT_DEMOS[p.id]?.brand;
  return (
    <article
      className="grid content-start gap-2 rounded-md border border-line bg-surface p-3"
      data-testid={`prebuilt-card-${p.id}`}
      aria-label={p.experience.name}
    >
      <div className="pointer-events-none rounded-sm bg-sunken p-1" aria-hidden="true" inert>
        <CardThumb p={p} doc={doc} />
      </div>
      <h3 className="text-sm font-semibold">{p.experience.name}</h3>
      <p className="text-xs text-muted">{p.experience.description}</p>
      {brand ? <p className="text-xs text-muted">Ejemplo: {brand} (marca ficticia).</p> : null}
      <p className="text-xs text-muted">
        <span className="font-medium text-fg">Tamaños:</span> {sizes.join(" · ")}
      </p>
      {p.requires === "video" ? (
        <p className="text-xs text-muted">Necesita un video tuyo (MP4 o WebM, hasta 25 MB).</p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            onUse(p);
          }}
        >
          Usar este formato
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onDemo(p);
          }}
        >
          <Eye aria-hidden="true" /> Ver demostración
        </Button>
      </div>
    </article>
  );
}

const MEDIA_LABELS: Record<MediaSlot, string> = {
  image: "Imagen principal",
  logo: "Logotipo",
  video: "Video",
  pictures: "Imágenes",
};

/** Simple configuration of the chosen format: its fields, its pictures and a live preview. No model is called. */
export function PrebuiltConfigurator({
  orgId,
  brandId,
  p,
  format,
  name,
  values,
  media,
  local,
  onValues,
  onMedia,
  onLocal,
}: {
  orgId: string;
  brandId: string | null;
  p: Prebuilt;
  format: string;
  name: string;
  values: PrebuiltValues;
  media: PrebuiltMedia;
  local: LocalFiles;
  onValues: (v: PrebuiltValues) => void;
  onMedia: (m: PrebuiltMedia) => void;
  onLocal: (files: LocalFiles) => void;
}) {
  const built = useMemo(() => {
    const base = baseDocument(p.category, format, name);
    if (!base) return { doc: null, problem: "Formato no válido." };
    if (p.requires === "video" && !media.video) return { doc: null, problem: "Elige o sube el video del anuncio." };
    try {
      return { doc: buildPrebuilt(p, base, values, media), problem: null };
    } catch {
      return { doc: null, problem: "Revisa los valores: con estos no se puede armar el anuncio." };
    }
  }, [p, format, name, values, media]);
  const set = (key: string, value: string | number | undefined) => {
    onValues({ ...values, [key]: value });
  };
  return (
    <PreviewContext orgId={orgId} local={local}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-4" data-testid="prebuilt-configurator">
          {p.fields.map((f) => (
            <Field key={f.key} label={f.label} {...(f.hint ? { hint: f.hint } : {})}>
              {f.kind === "color" ? (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label={f.label}
                    value={
                      typeof values[f.key] === "string"
                        ? (values[f.key] as string)
                        : ((p.defaults[f.key] as string | undefined) ?? "#000000")
                    }
                    onChange={(e) => {
                      set(f.key, e.target.value);
                    }}
                    className="h-8 w-12 cursor-pointer rounded border border-line"
                  />
                  <span className="text-xs text-muted">{String(values[f.key] ?? p.defaults[f.key] ?? "")}</span>
                </div>
              ) : (
                <Input
                  type={f.kind === "number" ? "number" : f.kind === "url" ? "url" : "text"}
                  value={values[f.key] === undefined ? "" : String(values[f.key])}
                  placeholder={
                    f.kind === "number"
                      ? String(p.defaults[f.key] ?? "")
                      : ((p.defaults[f.key] as string | undefined) ?? "")
                  }
                  {...(f.min !== undefined ? { min: f.min } : {})}
                  {...(f.max !== undefined ? { max: f.max } : {})}
                  {...(f.maxLength !== undefined ? { maxLength: f.maxLength } : {})}
                  onChange={(e) => {
                    set(
                      f.key,
                      f.kind === "number"
                        ? e.target.value === ""
                          ? undefined
                          : Number(e.target.value)
                        : e.target.value
                    );
                  }}
                />
              )}
            </Field>
          ))}
          {p.media.map((slot) => (
            <MediaPicker
              key={slot}
              orgId={orgId}
              brandId={brandId}
              slot={slot}
              p={p}
              media={media}
              onMedia={onMedia}
              local={local}
              onLocal={onLocal}
              values={values}
            />
          ))}
        </div>
        <div className="grid content-start gap-2 lg:sticky lg:top-4 lg:self-start">
          <p className="text-sm font-semibold">Vista previa</p>
          <p className="text-xs text-muted">
            Pruébala: es el anuncio real que se creará. Cambiar estos valores no usa créditos de IA.
          </p>
          <div className="rounded-md border border-line bg-sunken p-2" data-testid="prebuilt-preview">
            {built.doc ? (
              <Player doc={built.doc} box={380} />
            ) : (
              <p className="p-4 text-sm text-muted">{built.problem}</p>
            )}
          </div>
        </div>
      </div>
    </PreviewContext>
  );
}

function MediaPicker({
  orgId,
  brandId,
  slot,
  p,
  media,
  values,
  onMedia,
  local,
  onLocal,
}: {
  orgId: string;
  brandId: string | null;
  slot: MediaSlot;
  p: Prebuilt;
  media: PrebuiltMedia;
  values: PrebuiltValues;
  onMedia: (m: PrebuiltMedia) => void;
  local: LocalFiles;
  onLocal: (files: LocalFiles) => void;
}) {
  const kind = slot === "video" ? "video" : "image";
  const library = useBrandAssets(orgId, brandId ?? "", kind, brandId !== null);
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const many = slot === "pictures";
  const limit = many
    ? p.id === "carousel"
      ? Number(values.slides ?? p.defaults.slides ?? 3)
      : Number(values.pairs ?? p.defaults.pairs ?? 4)
    : 1;
  const chosen: readonly PickedAsset[] = many ? (media.pictures ?? []) : media[slot] ? [media[slot]] : [];
  const pick = (asset: NonNullable<typeof library.data>["items"][number]) => {
    choose({ ...pinnedRef(asset, "content"), name: asset.name });
  };
  const choose = (ref: PickedAsset) => {
    if (!many) {
      onMedia({ ...media, [slot]: chosen[0]?.revisionId === ref.revisionId ? undefined : ref });
      return;
    }
    const has = chosen.some((c) => c.revisionId === ref.revisionId);
    const next = has ? chosen.filter((c) => c.revisionId !== ref.revisionId) : [...chosen, ref].slice(0, limit);
    onMedia({ ...media, pictures: next });
  };
  const upload = async (file: File) => {
    setBusy(true);
    if (!brandId) {
      // No brand yet: keep the file here; it is uploaded to the new brand's library when the creative is created.
      try {
        const ref = await describeLocal(file, kind);
        onLocal(new Map([...local, [ref.revisionId, file]]));
        choose(ref);
      } catch (problem) {
        toast.error(`«${file.name}»: ${problem instanceof Error ? problem.message : "no se pudo leer."}`);
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const asset = await uploadAsset(orgId, brandId, file);
      if (asset.kind !== kind) toast.error(`«${file.name}» no es ${kind === "video" ? "un video" : "una imagen"}.`);
      else {
        toast.success(`«${asset.name}» está en la biblioteca de la marca.`);
        await queryClient.invalidateQueries({ queryKey: studioKeys.assets(orgId, brandId, kind) });
        pick(asset);
      }
    } catch (problem) {
      toast.error(
        `«${file.name}»: ${problem instanceof ApiProblem ? problem.message : "no se pudo subir. Inténtalo de nuevo."}`
      );
    } finally {
      setBusy(false);
    }
  };
  const label = `${MEDIA_LABELS[slot]}${many ? ` (hasta ${String(limit)})` : ""}${p.requires === slot ? "" : " · opcional"}`;
  return (
    <fieldset className="grid gap-2" data-testid={`media-${slot}`}>
      <legend className="text-sm font-medium">{label}</legend>
      <>
        <div className="flex flex-wrap gap-2">
          {chosen
            .filter((c) => local.has(c.revisionId))
            .map((c) => (
              <button
                key={c.revisionId}
                type="button"
                aria-pressed={true}
                aria-label={c.name}
                title={`${c.name} · se sube a la biblioteca de tu marca al crear el creativo`}
                onClick={() => {
                  choose(c);
                }}
                className="grid size-16 place-items-center overflow-hidden rounded border border-brand bg-sunken p-1 text-center text-[10px] ring-2 ring-brand"
              >
                {kind === "video" ? (
                  <Film className="size-4" aria-hidden="true" />
                ) : (
                  <ImageIcon className="size-4" aria-hidden="true" />
                )}
                {c.name.slice(0, 14)}
              </button>
            ))}
          {(brandId ? (library.data?.items ?? []) : []).slice(0, 24).map((a) => {
            const on = chosen.some((c) => c.assetId === a.id);
            const r = a.currentRevision;
            return (
              <button
                key={a.id}
                type="button"
                aria-pressed={on}
                aria-label={a.name}
                title={a.name}
                onClick={() => {
                  pick(a);
                }}
                className={cn(
                  "grid size-16 place-items-center overflow-hidden rounded border bg-sunken text-[10px]",
                  on ? "border-brand ring-2 ring-brand" : "border-line"
                )}
              >
                {kind === "image" && r ? (
                  <img
                    src={assetUrlFor(orgId)({ assetId: a.id, revisionId: r.id })}
                    alt=""
                    className="size-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span className="grid place-items-center gap-1 p-1 text-center">
                    <Film className="size-4" aria-hidden="true" />
                    {a.name.slice(0, 14)}
                  </span>
                )}
              </button>
            );
          })}
          {library.isLoading ? <Loader2 className="size-4 animate-spin" aria-label="Cargando la biblioteca" /> : null}
        </div>
        <div>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : kind === "video" ? (
              <Upload aria-hidden="true" />
            ) : (
              <ImageIcon aria-hidden="true" />
            )}
            {busy ? "Subiendo…" : kind === "video" ? "Subir un video" : "Subir una imagen"}
          </Button>
          <input
            ref={input}
            type="file"
            accept={ACCEPT[kind].join(",")}
            className="sr-only"
            tabIndex={-1}
            aria-label={kind === "video" ? "Elegir un video de tu computadora" : "Elegir una imagen de tu computadora"}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void upload(file);
            }}
          />
        </div>
        <p className="text-xs text-muted">
          {kind === "video"
            ? `MP4 o WebM, hasta ${String(ASSET_UPLOAD_LIMITS.video / 1024 / 1024)} MB. Se reproduce sin sonido hasta que la persona lo active.`
            : `PNG, JPG o WebP, hasta ${String(ASSET_UPLOAD_LIMITS.image / 1024 / 1024)} MB.`}
          {brandId ? null : " Se guarda en la biblioteca de tu marca al crear el creativo."}
        </p>
      </>
    </fieldset>
  );
}
